import { Injectable } from '@angular/core';
import config from '@config';
import { DateService } from '@services/date.service';
import { PatientAssistanceRequestViewModel } from '@services/models/patient-assistance-request-view.model';
import { DateUtility } from '@shared/date-utility';
import { ClaimOverrideRejectReason } from '@shared/models/claim-override-reject-reason.model';
import { CodeRequestResultType } from '@shared/models/code-request-result.enum';
import { CopayCodeRequestFailures } from '@shared/models/copay-code-request-failures.enum';
import { ICopayCodeRequest } from '@shared/models/copay-code-request.model';
import { CopayPaStatus } from '@shared/models/copay-pa-status.enum';
import { CopayRequestSchema, ExtendedCopayRequestSchema } from '@shared/models/copay-request-schema';
import { StatusIconType } from '@shared/models/status-icon.type';
import Utils from '@shared/providers/utils';
import { get } from 'lodash';
import moment from 'moment-timezone';

@Injectable({
  providedIn: 'root',
})
export class CopayCodeRequestUtilityService {
  static readonly UNDEFINED_TEXT = 'undefined';
  static readonly ABANDON_TIME_MINUTES = 60;

  constructor(private dateService: DateService) {}

  hasCodeRequestDispensedCodes(codeRequest: ICopayCodeRequest): boolean {
    return codeRequest && codeRequest.number_codes_dispensed > 0;
  }

  hasCodeRequestBeenCompleted(codeRequest: ICopayCodeRequest): boolean {
    return codeRequest && 'end' in codeRequest;
  }

  hasCodeRequestBeenAbandoned(codeRequest: ICopayCodeRequest): boolean {
    if (this.hasCodeRequestBeenCompleted(codeRequest)) {
      return false;
    }

    const CODE_REQUEST_START = moment(codeRequest.created);
    const NOW_MINUS_ABANDON_TIME = moment().subtract(CopayCodeRequestUtilityService.ABANDON_TIME_MINUTES, 'minutes');
    return CODE_REQUEST_START.isBefore(NOW_MINUS_ABANDON_TIME);
  }

  isCodeRequestInProgress(codeRequest: ICopayCodeRequest): boolean {
    return this.dateService.getDurationBetweenObjectStartAndEnd(codeRequest) === DateService.IN_PROGRESS;
  }

  isCodeRequestSuccessful(codeRequest: ICopayCodeRequest): boolean {
    return this.hasCodeRequestDispensedCodes(codeRequest);
  }

  isCodeRequestInFinalState(codeRequest?: ICopayCodeRequest): boolean {
    if (!codeRequest) {
      return false;
    }
    let isFinalState = false;
    if (
      !!codeRequest.end ||
      codeRequest.consignment_authorization_id ||
      codeRequest.request_failure ||
      (codeRequest.dispensed_codes?.length ?? 0) > 0
    ) {
      isFinalState = true;
    }
    return isFinalState;
  }

  getCodeRequestStatus(codeRequest: ICopayCodeRequest): StatusIconType {
    const isSuccessful = this.isCodeRequestSuccessful(codeRequest);
    const isInProgress = this.isCodeRequestInProgress(codeRequest);

    if (isSuccessful && isInProgress) {
      return 'maybe-good';
    } else if (isSuccessful) {
      return 'good';
    } else if (!isSuccessful && isInProgress) {
      return 'maybe-bad';
    }
    return 'bad';
  }

  /**
   * @deprecated
   * @see getCopayRequestStatusClass
   */
  getCopayRequestStatusClass(copayRequest: PatientAssistanceRequestViewModel) {
    return CopayCodeRequestUtilityService.getCopayRequestStatusClass(copayRequest);
  }

  static getCopayRequestStatusClass(copayRequest: PatientAssistanceRequestViewModel) {
    return (
      {
        [CodeRequestResultType.AppealComplete]: 'success',
        [CodeRequestResultType.PaApproved]: 'success',
        [CodeRequestResultType.Paid]: 'success',
        [CodeRequestResultType.BrightLinkNotEligible]: 'danger',
        [CodeRequestResultType.Rejected]: 'danger',
        [CodeRequestResultType.Duplicate]: 'warning',
        [CodeRequestResultType.Incomplete]: 'warning',
        [CodeRequestResultType.Reversed]: 'warning',
      }[copayRequest.status] ?? 'info'
    );
  }

  didRejectCodeForDispenseFail(dispenseCodeRequestResponse: ICopayCodeRequest): boolean {
    return dispenseCodeRequestResponse && dispenseCodeRequestResponse.request_failure === CopayCodeRequestFailures.NonAllowableRejectCode;
  }

  mapRequestToExtendedRequest(item: CopayRequestSchema): ExtendedCopayRequestSchema {
    const request: ExtendedCopayRequestSchema = {
      ...item,
      watched: false,
      isConsignment: item.isForConsignment === 1,
      isCoupon: item.numberCodesDispensed > 0,
      isRejected: Utils.isNonEmptyString(item.requestFailure ?? ''),
      inProgress: this.isCodeRequestInProgress(item as any),
    };
    if (request.isConsignment) {
      request.result = 'Consignment';
    } else if (request.isCoupon) {
      request.result = 'Coupon';
    } else if (request.isRejected) {
      request.result = 'Rejected';
    }
    return request;
  }

  isRequestReadyForConsignment(request: Partial<CopayRequestSchema>): boolean {
    if (
      request.paStatus === CopayPaStatus.Approved ||
      request.paStatus === CopayPaStatus.Denied ||
      request.paStatus === CopayPaStatus.Cancelled
    ) {
      return true;
    }
    if (!request.consignmentAuthorizationIdAvailable) {
      return false;
    }
    const availableDate = moment(request.consignmentAuthorizationIdAvailable);
    const now = moment.utc();
    return now.isSameOrAfter(availableDate) && request.paStatus === CopayPaStatus.InProgress;
  }

  isBrightClockReleaseAvailable(claim: PatientAssistanceRequestViewModel, history: PatientAssistanceRequestViewModel[]): boolean {
    const pendingClaim = this.getBrightClockPendingClaim(history);
    return (
      (pendingClaim &&
        (claim.requestFailure?.includes('LEFT UNTIL REPLENISHMENT IS AVAILABLE FOR THIS RX') ||
          claim.requestFailure?.includes('IF PA DENIAL OCCURS BEFORE THE END OF THE WAIT TIME'))) ??
      false
    );
  }

  getClaimOverrideRejectReason(claim: ICopayCodeRequest): ClaimOverrideRejectReason | undefined {
    return config.claimOverrideRejectReasons.find(
      reason =>
        reason.resultIds.some(resultId => resultId === claim.result_id) ||
        (reason.rejectId && reason.rejectId === claim.reject_id) ||
        reason.rejectReasonMessages?.find(msg => claim.request_failure?.includes(msg)),
    );
  }

  isClaimOverrideRejectReasonSent(claim: ICopayCodeRequest): boolean {
    const reason = this.getClaimOverrideRejectReason(claim);

    if (!reason) {
      return false;
    }

    const flagPrefix = this.getBypassFlagPrefix(claim, reason);
    return get(claim, flagPrefix) === true;
  }

  getBrightClockPendingClaim(history: PatientAssistanceRequestViewModel[]): PatientAssistanceRequestViewModel | undefined {
    const pendingClaim = history.find(
      item => !!item.consignmentAuthorizationIdAvailable && new Date(item.consignmentAuthorizationIdAvailable).getTime() > Date.now(),
    );
    return pendingClaim;
  }

  getOverrideInfo(claim?: ICopayCodeRequest | null): { user: string; date: string; reason: string } {
    const reason = claim ? this.getClaimOverrideRejectReason(claim) : undefined;

    if (!reason || !claim) {
      return { user: 'UNKNOWN', date: 'UNKNOWN', reason: 'UNKNOWN' };
    }

    const flagPrefix = this.getBypassFlagPrefix(claim, reason);
    const date = get(claim, `${flagPrefix}_granted_at`);

    return {
      user: get(claim, `${flagPrefix}_granted_by`) ?? 'UNKNOWN',
      date: date ? DateUtility.formatDate(date, DateUtility.US_DATE_TIME_FORMAT) : 'UNKNOWN',
      reason: get(claim, `${flagPrefix}_reason`) ?? 'UNKNOWN',
    };
  }

  private getBypassFlagPrefix(claim: ICopayCodeRequest, reason: ClaimOverrideRejectReason): string {
    const flagPrefix = `bypass_${reason.flag}_on_resubmission`;

    if (reason.oldFlag && !get(claim, flagPrefix)) {
      const oldFlagPrefix = `bypass_${reason.oldFlag}_on_resubmission`;

      if (get(claim, oldFlagPrefix)) {
        return oldFlagPrefix;
      }
    }

    return flagPrefix;
  }
}
